import React, { useMemo, useState } from 'react';
import MapLibreMap from './charts/MapLibreMap';

const ALERT_COLORS = {
  NOMINAL: '#818CF8',
  ELEVATED: '#F59E0B',
  DISRUPTIVE: '#F97316',
  CRITICAL: '#EF4444',
};

const DOMAIN_LABELS = {
  airport_operations: 'AVIATION',
  port_operations: 'MARITIME',
  road_transport: 'ROAD',
  utilities: 'UTILITIES',
  telecommunications: 'TELECOM',
  logistics_supply_chain: 'LOGISTICS',
  emergency_systems: 'EMERGENCY',
  public_safety: 'PUBLIC',
};

const ALL_DOMAINS = ['all', ...Object.keys(DOMAIN_LABELS)];

export default function VegaCenterPanel({ insights, offlineNotices, selectedInsightId, onSelectInsight, scoring }) {
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all'
    ? insights
    : insights.filter(i => i.domain === filter);

  // Map pins — only insights with actual coordinates. Severity maps to colour.
  const ALERT_TO_SEVERITY = { NOMINAL: 'nominal', ELEVATED: 'elevated', DISRUPTIVE: 'storm', CRITICAL: 'critical' };
  const mapPins = useMemo(
    () => (filtered || [])
      .filter((i) => typeof i.latitude === 'number' && typeof i.longitude === 'number')
      .map((i) => ({
        id: i.id,
        lat: i.latitude,
        lon: i.longitude,
        severity: ALERT_TO_SEVERITY[i.alert] || 'nominal',
        label: i.topic || i.id,
      })),
    [filtered]
  );

  const domainStatusMap = {};
  for (const ins of insights) {
    const dom = ins.domain;
    if (!domainStatusMap[dom] || ins.severity > domainStatusMap[dom].severity) {
      domainStatusMap[dom] = ins;
    }
  }

  return (
    <div className="vega-center" data-testid="vega-center-panel">
      {/* Infrastructure Health Grid */}
      <div className="vega-section-head">
        <span className="vega-section-title">INFRASTRUCTURE HEALTH</span>
        <span className="vega-section-meta">{Object.keys(DOMAIN_LABELS).length} domains</span>
      </div>
      <div className="vega-infra-grid" data-testid="vega-infra-grid">
        {Object.entries(DOMAIN_LABELS).map(([key, label]) => {
          const top = domainStatusMap[key];
          const alert = top ? (top.alert || 'NOMINAL') : 'NOMINAL';
          const color = ALERT_COLORS[alert] || '#818CF8';
          const count = insights.filter(i => i.domain === key).length;
          return (
            <div className="vega-infra-cell" key={key} data-testid={`vega-infra-${key}`}>
              <div className="vega-infra-cell-name">{label}</div>
              <div className="vega-infra-cell-status" style={{ color }}>{alert}</div>
              <div className="vega-infra-cell-count">{count} signal{count !== 1 ? 's' : ''}</div>
            </div>
          );
        })}
      </div>

      {/* Degraded strip for offline/limited sources */}
      {offlineNotices?.length > 0 && (
        <div className="vega-degraded-strip" data-testid="vega-degraded-strip">
          <div className="vega-degraded-icon">!</div>
          <div className="vega-degraded-text">
            {offlineNotices.length} source{offlineNotices.length > 1 ? 's' : ''} degraded
          </div>
          <div className="vega-degraded-note">
            {offlineNotices.map(n => n.sources?.[0] || n.topic?.split('—')[0]).join(', ')}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="vega-section-head">
        <span className="vega-section-title">FILTERS</span>
        <span className="vega-section-meta" data-testid="vega-filter-count">{filtered.length} ACTIVE</span>
      </div>
      <div className="vega-filter-bar" data-testid="vega-filter-bar">
        {ALL_DOMAINS.map(d => (
          <button
            key={d}
            className={`vega-filter-btn ${filter === d ? 'active' : ''}`}
            onClick={() => setFilter(d)}
            data-testid={`vega-filter-${d}`}
          >
            {d === 'all' ? 'ALL' : DOMAIN_LABELS[d] || d.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Geographic distribution — react-map-gl / maplibre */}
      {mapPins.length > 0 && (
        <>
          <div className="vega-section-head">
            <span className="vega-section-title">GEOGRAPHIC DISTRIBUTION</span>
            <span className="vega-section-meta">{mapPins.length} pin{mapPins.length === 1 ? '' : 's'}</span>
          </div>
          <div className="vega-map-wrap" data-testid="vega-map-wrap" style={{ margin: '0 12px 12px' }}>
            <MapLibreMap
              pins={mapPins}
              height={240}
              onPinClick={(p) => onSelectInsight(p.id)}
              testId="vega-maplibre-map"
            />
          </div>
        </>
      )}

      {/* Insight feed */}
      <div className="vega-section-head">
        <span className="vega-section-title">SIGNAL FEED</span>
        <span className="vega-section-meta">{filtered.length} signal{filtered.length === 1 ? '' : 's'}</span>
      </div>
      <div className="vega-feed" data-testid="vega-feed">
        {filtered.length === 0 ? (
          <div className="vega-empty" data-testid="vega-empty-feed">NO ACTIVE SIGNALS</div>
        ) : (
          filtered.map(ins => {
            const alertColor = ALERT_COLORS[ins.alert] || '#818CF8';
            const isSelected = ins.id === selectedInsightId;
            return (
              <div
                key={ins.id}
                className={`vega-insight-card ${isSelected ? 'selected' : ''}`}
                style={{ borderLeftColor: isSelected ? alertColor : 'transparent' }}
                onClick={() => onSelectInsight(ins.id)}
                data-testid={`vega-insight-${ins.id}`}
              >
                <div className="vega-ic-header">
                  <span className="vega-ic-badge" style={{ color: alertColor, borderColor: `${alertColor}60` }}>
                    {ins.alert}
                  </span>
                  <span className="vega-ic-domain">
                    {DOMAIN_LABELS[ins.domain] || ins.domain?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  <span className="vega-ic-conf">{(ins.confidence * 100).toFixed(0)}%</span>
                </div>
                <div className="vega-ic-topic">{ins.topic}</div>
                {ins.impact && <div className="vega-ic-impact">{ins.impact}</div>}
                <div className="vega-ic-meta">
                  <span className="vega-ic-sources">{(ins.sources || []).join(', ')}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
